import type { ReactNode } from 'react';
import { createContext, useEffect, useState } from 'react';

import { useGetExtranetHotelInfo } from 'app/hooks/data/core/extranet/hotel/useGetHotelInfo';
import { useHotelManager } from 'app/hooks/data/extranet/useHotelManager';
import type { AdminUser } from 'app/typings';
import { ChannelManager } from 'app/typings/channelManagers';
import type { Hotel } from 'app/typings/hotels/Hotel/Hotel';
import { amplitudeSetHotel } from 'app/utils/track/amplitude';

import { LegacyHotelContextProvider } from './LegacyHotelContext';
import { ModernHotelContextProvider } from './ModernHotelContext';

type HotelContextType = {
  hotelId: number;
  hotelInfo?: Hotel;
  isLegacy: boolean;
  hotelLoading: boolean;
  setHotelId: (hotelId: number) => void;
  manager?: AdminUser | null;
  hasChannelManager: boolean;
};

export const HotelContext = createContext<HotelContextType>({
  hotelId: 1,
  hotelInfo: undefined,
  isLegacy: true,
  hotelLoading: true,
  setHotelId: () => false,
  manager: undefined,
  hasChannelManager: false,
});

type HotelContextProviderProps = {
  urlHotelId: number;
  children: ReactNode;
};

export const HotelContextProvider = ({
  urlHotelId,
  children,
}: HotelContextProviderProps) => {
  const [hotelId, setHotelId] = useState<number>(urlHotelId);
  const { data: hotelInfo, isFetching: hotelLoading } =
    useGetExtranetHotelInfo(hotelId);
  const { data: manager } = useHotelManager(hotelInfo?.id);

  useEffect(() => {
    amplitudeSetHotel({
      hotelId,
      isLegacy: hotelInfo ? hotelInfo.isLegacy : true,
      channelManager: hotelInfo?.channelManager,
    });
  }, [hotelId, hotelInfo]);

  const isLegacy = hotelInfo ? hotelInfo.isLegacy : true;

  const hasChannelManager = !!(
    hotelInfo?.channelManager &&
    hotelInfo?.channelManager !== ChannelManager.NONE
  );

  return (
    <HotelContext.Provider
      value={{
        hotelId,
        hotelInfo,
        isLegacy,
        hotelLoading,
        setHotelId,
        manager,
        hasChannelManager,
      }}
    >
      {!hotelInfo ? (
        children
      ) : isLegacy ? (
        <LegacyHotelContextProvider>{children}</LegacyHotelContextProvider>
      ) : (
        <ModernHotelContextProvider>{children}</ModernHotelContextProvider>
      )}
    </HotelContext.Provider>
  );
};
